/**
 * Base error class for SubscribeDev client errors
 */
export class SubscribeDevError extends Error {
  public readonly code: string
  public readonly statusCode?: number
  public readonly details?: Record<string, any>
  public readonly requestId?: string

  constructor(
    message: string,
    code: string,
    statusCode?: number,
    details?: Record<string, any>,
    requestId?: string
  ) {
    super(message)
    this.name = 'SubscribeDevError'
    this.code = code
    this.statusCode = statusCode
    this.details = details
    this.requestId = requestId
  }
}

/**
 * Error thrown when account has insufficient balance
 */
export class InsufficientBalanceError extends SubscribeDevError {
  constructor(
    message: string,
    remainingCredits?: number,
    requiredCredits?: number,
    requestId?: string
  ) {
    super(message, 'INSUFFICIENT_BALANCE', 402, {
      remainingCredits,
      requiredCredits
    }, requestId)
    this.name = 'InsufficientBalanceError'
  }
}

/**
 * Error thrown when rate limit is exceeded
 */
export class RateLimitError extends SubscribeDevError {
  public readonly resetTime?: string
  public readonly retryAfter?: number

  constructor(
    message: string,
    resetTime?: string,
    retryAfter?: number,
    requestId?: string
  ) {
    super(message, 'RATE_LIMIT_EXCEEDED', 429, {
      resetTime,
      retryAfter
    }, requestId)
    this.name = 'RateLimitError'
    this.resetTime = resetTime
    this.retryAfter = retryAfter
  }
}

/**
 * Error thrown for authentication failures
 */
export class AuthenticationError extends SubscribeDevError {
  constructor(message: string, requestId?: string) {
    super(message, 'AUTHENTICATION_FAILED', 401, undefined, requestId)
    this.name = 'AuthenticationError'
  }
}

/**
 * Error thrown when access to a resource is denied
 */
export class AccessDeniedError extends SubscribeDevError {
  constructor(message: string, requestId?: string) {
    super(message, 'ACCESS_DENIED', 403, undefined, requestId)
    this.name = 'AccessDeniedError'
  }
}

/**
 * Error thrown when a resource is not found
 */
export class NotFoundError extends SubscribeDevError {
  constructor(message: string, requestId?: string) {
    super(message, 'NOT_FOUND', 404, undefined, requestId)
    this.name = 'NotFoundError'
  }
}

/**
 * Error thrown for request validation failures
 */
export class ValidationError extends SubscribeDevError {
  constructor(message: string, details?: Record<string, any>, requestId?: string) {
    super(message, 'VALIDATION_ERROR', 400, details, requestId)
    this.name = 'ValidationError'
  }
}

/**
 * Error thrown when user is not subscribed (unsubscribed 402 error)
 */
export class UnsubscribedError extends SubscribeDevError {
  constructor(message: string, requestId?: string) {
    super(message, 'USER_UNSUBSCRIBED', 402, undefined, requestId)
    this.name = 'UnsubscribedError'
  }
}

/**
 * Error thrown when operation exceeds user's plan limit
 */
export class PlanUpgradeRequiredError extends SubscribeDevError {
  public readonly upgradeUrl?: string
  public readonly currentPlan?: string
  public readonly currentLimit?: number
  public readonly required?: number

  constructor(
    message: string,
    upgradeUrl?: string,
    currentPlan?: string,
    currentLimit?: number,
    required?: number,
    requestId?: string
  ) {
    super(message, 'PLAN_UPGRADE_REQUIRED', 402, {
      upgradeUrl,
      currentPlan,
      currentLimit,
      required
    }, requestId)
    this.name = 'PlanUpgradeRequiredError'
    this.upgradeUrl = upgradeUrl
    this.currentPlan = currentPlan
    this.currentLimit = currentLimit
    this.required = required
  }
}

/**
 * Error thrown when a workflow is temporarily unavailable due to owner credits
 */
export class WorkflowUnavailableError extends SubscribeDevError {
  constructor(message: string, requestId?: string) {
    super(message, 'WORKFLOW_UNAVAILABLE', 503, undefined, requestId)
    this.name = 'WorkflowUnavailableError'
  }
}